/**
 * ManageCategoriesModal — v3.0.141
 * CRUD UI for user-created song categories. Rename inline, delete with
 * confirm, add new via a compact form at the top. Emoji + color picker.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Loader2, Trash2, Check, X, Pencil,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { apiFetch } from '../App';

const COLORS = [
  { key: 'emerald', className: 'bg-emerald-500' },
  { key: 'teal',    className: 'bg-teal-500' },
  { key: 'sky',     className: 'bg-sky-500' },
  { key: 'purple',  className: 'bg-purple-500' },
  { key: 'pink',    className: 'bg-pink-500' },
  { key: 'rose',    className: 'bg-rose-500' },
  { key: 'amber',   className: 'bg-amber-500' },
  { key: 'orange',  className: 'bg-orange-500' },
  { key: 'lime',    className: 'bg-lime-500' },
  { key: 'slate',   className: 'bg-slate-500' },
];

// A small curated emoji set — no emoji picker library dependency.
const EMOJIS = [
  '', '🎹', '🎸', '🥁', '🎤', '🎧', '🎼', '🎵', '🎶',
  '⭐', '🌟', '💜', '🔥', '💎', '🌈', '☀️', '🌙',
  '🎂', '💍', '👶', '🐾', '🌷', '🌊', '🕊️', '🎨',
];

export default function ManageCategoriesModal({ open, onOpenChange, onChange }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('');
  const [color, setColor] = useState('emerald');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiFetch('/categories');
      setCategories(Array.isArray(data?.custom) ? data.custom : []);
    } catch (err) {
      toast.error(`Load failed: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const notify = () => { if (onChange) onChange(); };

  const create = async () => {
    const n = name.trim();
    if (!n) { toast.error('Name is required'); return; }
    setCreating(true);
    try {
      await apiFetch('/categories', {
        method: 'POST',
        body: JSON.stringify({ name: n, emoji, color }),
      });
      toast.success(`Created "${n}"`);
      setName(''); setEmoji(''); setColor('emerald');
      await load();
      notify();
    } catch (err) {
      toast.error(err.message || String(err));
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (c) => { setEditingId(c.category_id); setEditingName(c.name); };
  const cancelEdit = () => { setEditingId(null); setEditingName(''); };

  const saveEdit = async () => {
    const n = editingName.trim();
    if (!n) { cancelEdit(); return; }
    try {
      await apiFetch(`/categories/${editingId}`, {
        method: 'PATCH', body: JSON.stringify({ name: n }),
      });
      toast.success('Renamed');
      cancelEdit();
      await load();
      notify();
    } catch (err) {
      toast.error(err.message || String(err));
    }
  };

  const changeColor = async (id, nextColor) => {
    try {
      await apiFetch(`/categories/${id}`, {
        method: 'PATCH', body: JSON.stringify({ color: nextColor }),
      });
      await load();
      notify();
    } catch (err) {
      toast.error(err.message || String(err));
    }
  };

  const changeEmoji = async (id, nextEmoji) => {
    try {
      await apiFetch(`/categories/${id}`, {
        method: 'PATCH', body: JSON.stringify({ emoji: nextEmoji }),
      });
      await load();
      notify();
    } catch (err) {
      toast.error(err.message || String(err));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Songs keep playing — they just lose this tag.`)) return;
    try {
      await apiFetch(`/categories/${c.category_id}`, { method: 'DELETE' });
      toast.success(`Deleted "${c.name}"`);
      await load();
      notify();
    } catch (err) {
      toast.error(err.message || String(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-zinc-950 border-white/10 text-white max-w-xl"
        data-testid="manage-categories-modal"
        onEscapeKeyDown={(e) => {
          // v3.0.142 — When the user is inline-renaming a category,
          // Esc should only cancel the rename, NOT close the whole
          // modal. Cancel the rename here and swallow the event.
          if (editingId) {
            e.preventDefault();
            cancelEdit();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Manage categories</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Create your own labels beyond Favorites and Archive. Songs can be
            in multiple categories at once.
          </DialogDescription>
        </DialogHeader>

        {/* Create form */}
        <div className="bg-white/5 border border-white/10 rounded-md p-3 space-y-3">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">
            New category
          </div>
          <div className="flex gap-2">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 40))}
              onKeyDown={(e) => e.key === 'Enter' && create()}
              placeholder="e.g. Instrumental, Wedding Set, Kids…"
              className="flex-1 bg-black/40 border-white/10 text-white"
              data-testid="new-category-name-input"
            />
            <Button
              onClick={create}
              disabled={creating || !name.trim()}
              className="bg-emerald-500 text-black hover:bg-emerald-400"
              data-testid="new-category-create-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {EMOJIS.map((e, i) => (
              <button
                key={`${i}-${e}`}
                onClick={() => setEmoji(e)}
                className={`w-7 h-7 rounded flex items-center justify-center text-base leading-none transition ${
                  emoji === e ? 'bg-white/15 ring-1 ring-emerald-400' : 'hover:bg-white/10'
                } ${e === '' ? 'text-[10px] text-zinc-500 uppercase' : ''}`}
                title={e || 'no emoji'}
                data-testid={`emoji-pick-${i}`}
              >
                {e || 'none'}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {COLORS.map((c) => (
              <button
                key={c.key}
                onClick={() => setColor(c.key)}
                className={`w-6 h-6 rounded-full ${c.className} transition ${
                  color === c.key ? 'ring-2 ring-white/70 ring-offset-2 ring-offset-zinc-950' : ''
                }`}
                title={c.key}
                data-testid={`color-pick-${c.key}`}
              />
            ))}
          </div>
        </div>

        {/* Existing list */}
        <div className="max-h-[45vh] overflow-y-auto space-y-1">
          {loading ? (
            <div className="py-6 text-center text-sm text-zinc-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-8 text-sm text-zinc-500 border border-dashed border-white/10 rounded-md">
              No custom categories yet — create your first one above.
            </div>
          ) : (
            categories.map((c) => (
              <div
                key={c.category_id}
                className="flex items-center gap-2 px-2 py-2 rounded-md bg-white/5 border border-white/5"
                data-testid={`category-row-${c.category_id}`}
              >
                {/* Emoji picker for existing row */}
                <details className="relative group">
                  <summary className="list-none cursor-pointer w-8 h-8 rounded flex items-center justify-center text-base hover:bg-white/10">
                    {c.emoji || '📌'}
                  </summary>
                  <div className="absolute left-0 top-9 z-10 bg-zinc-900 border border-white/10 rounded-md p-1.5 flex flex-wrap gap-1 w-56 shadow-xl">
                    {EMOJIS.map((e, i) => (
                      <button
                        key={`row-${c.category_id}-emoji-${i}`}
                        onClick={(ev) => {
                          ev.currentTarget.closest('details').removeAttribute('open');
                          changeEmoji(c.category_id, e);
                        }}
                        className="w-6 h-6 rounded flex items-center justify-center text-sm hover:bg-white/15"
                      >
                        {e || '—'}
                      </button>
                    ))}
                  </div>
                </details>

                {/* Name (edit inline) */}
                <div className="flex-1 min-w-0">
                  {editingId === c.category_id ? (
                    <div className="flex gap-1">
                      <Input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value.slice(0, 40))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit();
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        autoFocus
                        className="flex-1 h-8 bg-black/40 border-white/10 text-white"
                        data-testid={`category-rename-input-${c.category_id}`}
                      />
                      <Button size="sm" variant="ghost" onClick={saveEdit} className="h-8 w-8 p-0" data-testid={`category-rename-save-${c.category_id}`}>
                        <Check className="w-3.5 h-3.5 text-emerald-400" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={cancelEdit} className="h-8 w-8 p-0" data-testid={`category-rename-cancel-${c.category_id}`}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="truncate">{c.name}</span>
                      <span className="text-xs text-zinc-500 shrink-0">({c.song_count})</span>
                    </div>
                  )}
                </div>

                {/* Color palette (tiny inline) */}
                <div className="flex gap-0.5 shrink-0">
                  {COLORS.slice(0, 6).map((cl) => (
                    <button
                      key={`row-${c.category_id}-color-${cl.key}`}
                      onClick={() => changeColor(c.category_id, cl.key)}
                      className={`w-3.5 h-3.5 rounded-full ${cl.className} ${
                        c.color === cl.key ? 'ring-2 ring-white/70' : ''
                      }`}
                      title={cl.key}
                    />
                  ))}
                </div>

                {editingId !== c.category_id && (
                  <>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => startEdit(c)}
                      className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
                      title="Rename"
                      data-testid={`category-rename-${c.category_id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => remove(c)}
                      className="h-8 w-8 p-0 text-zinc-400 hover:text-rose-300"
                      title="Delete"
                      data-testid={`category-delete-${c.category_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
